import { defineRouter } from "#q-app"
import {
	createRouter,
	createMemoryHistory,
	createWebHistory,
	createWebHashHistory,
} from "vue-router"
import { pageview } from 'vue-gtag'

import useAuthentication from "src/composables/useAuthentication.js"
import { getUserPermissionSet } from "src/composables/usePermissions"

import routes from "src/router/routes.js"

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

export default defineRouter(function (/* { store, ssrContext } */) {
	const { isAuthenticated } = useAuthentication()

	const createHistory = import.meta.env.QUASAR_SERVER
		? createMemoryHistory
		: import.meta.env.QUASAR_VUE_ROUTER_MODE === "history"
			? createWebHistory
			: createWebHashHistory

	const Router = createRouter({
		// A query-only change is a state change, not a new page: opening a
		// detail drawer (?fix=, ?finding=), ticking a findings filter or
		// switching a tab all navigate, and scrolling to top would throw away
		// the reader's position in the list they just clicked. Same path means
		// stay put; everything else lands at the top as before. A link with a
		// hash lands on that element instead (e.g. a help page section), clear
		// of the fixed header.
		scrollBehavior: (to, from) => {
			if (to.hash) {
				return { el: to.hash, top: 72 }
			}
			return to.path === from.path ? false : { left: 0, top: 0 }
		},
		routes: routes,

		// Leave this as is and make changes in quasar.config.js instead!
		// quasar.config.js -> build -> vueRouterMode
		// quasar.config.js -> build -> publicPath
		history: createHistory(import.meta.env.QUASAR_VUE_ROUTER_BASE),
	})

	// Handle persistent query parameters.
	Router.beforeEach((to, from) => {
		//console.debug("router: beforeEach1: from:", from, "to:", to)

		// This is the list of persistent query parameters.
		// Any query parameter in this list will be persisted between navigation events.
		const globalParameters = [
			'tenantId',
			'tenantRecursive',
		]
		let changed = false
		for (let globalParameter of globalParameters) {
			if (from.query[globalParameter] && !to.query[globalParameter]) {
				to.query[globalParameter] = from.query[globalParameter]
				changed = true
			}
		}
		if (changed) {
			return to
		}
	})

	// Handle authorization for each page
	Router.beforeEach(async (to/*, from*/) => {
		//console.debug("router: beforeEach2: to:", to)

		if (to.meta && to.meta.requiresAuth) {
			try {
				// TODO: this causes a REST API request on each page change. Is there a more efficient way to do this?
				let authenticated = await isAuthenticated()
				//console.log("router: beforeEach: authenticated:", authenticated)
				if (!authenticated) {
					return { path: "/login", query: { redirect: to.fullPath } }
				}

				if (Array.isArray(to.meta.permissions) && to.meta.permissions.length > 0) {
					let userPermissionSet = getUserPermissionSet()

					for (let requiredPermission of to.meta.permissions) {
						if (!userPermissionSet[requiredPermission]) {
							console.warn("User is missing required permission:", requiredPermission)
							return { path: "/missing-permission/" + encodeURIComponent(requiredPermission), query: { redirect: to.fullPath } }
						}
					}
				}

			} catch {
				// Navigate to the login page on error.
				return { path: "/login", query: { redirect: encodeURIComponent(to.fullPath) } }
			}
		}

		return true
	})

	// Google Analytics
	//
	// We're going to use `beforeResolve` because this will _only_ be called once per route change.
	// `afterEach` *can* change multiple times as part of a `replace` action where only the path changes.
	Router.beforeResolve((to) => {
		// Path ONLY. 'to' carries 'hash' and 'to.fullPath' carries query+hash; an
		// external-login callback puts a session JWT in the fragment, so logging
		// the route object prints it to the console, and whenever 'to.matched' is
		// empty the fullPath fallback would ship it to Google Analytics.
		console.debug("router: beforeResolve (analytics):", to.path)
		let path = to.path
		if (Array.isArray(to.matched) && to.matched.length > 0) {
			path = to.matched[to.matched.length - 1].path
		}
		console.debug("router: beforeResolve (analytics): pageview(" + path + ")")
		// page_location MUST be passed explicitly. Given a bare string, vue-gtag
		// fills it in from window.location.href — which on the external-login
		// landing is "/#_callback=<base64 session JWT>", so the token would be
		// transmitted to Google Analytics on every SSO/Microsoft sign-in.
		pageview({ page_path: path, page_location: window.location.origin + path })
	})

	return Router
})
